import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, FolderOpen, SignOut, ArrowRight, Trash, Warning, Sparkle, Buildings } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import { INDUSTRIES, getIndustry } from "@/lib/industries";

const TIER_LABEL = { free: "Free", base: "Starter", pro: "Pro", enterprise: "Enterprise" };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [billing, setBilling] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [projectIndustry, setProjectIndustry] = useState(user?.industry || "general");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [proj, bill, ai] = await Promise.all([
        api.get("/projects"),
        api.get("/billing/me").catch(() => ({ data: null })),
        api.get("/me/ai-usage").catch(() => ({ data: null })),
      ]);
      setProjects(proj.data);
      setBilling(bill.data);
      setAiUsage(ai.data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name, description: desc });
      toast.success("Project created");
      setOpen(false); setName(""); setDesc("");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to create";
      const upgrade = err?.response?.status === 402;
      toast.error(detail, upgrade ? { action: { label: "Upgrade", onClick: () => navigate("/pricing") } } : undefined);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project and all its data?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Top bar */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={36} testid="dashboard-brand" tagline="AI-Driven Project Intelligence" />
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="hidden sm:inline-flex text-sm font-medium text-zinc-700 hover:text-zinc-950" data-testid="dashboard-pricing-link">Pricing</Link>
            {user?.is_admin && (
              <Link to="/admin" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-[#0020F5] hover:text-zinc-950" data-testid="dashboard-admin-link">
                ★ Admin
              </Link>
            )}
            <Link to="/account" className="hidden sm:inline-flex text-sm font-medium text-zinc-700 hover:text-zinc-950" data-testid="dashboard-account-link">Account</Link>
            <div className="hidden sm:block text-right">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-500">{user?.full_name}</div>
              <div className="text-xs text-zinc-500">{user?.email}</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-300 text-sm font-medium hover:bg-zinc-100"
              data-testid="logout-button"
            >
              <SignOut size={16} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Workspace</div>
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">Projects</h1>
            <p className="text-zinc-600 mt-2">Pick a project to run it through the six lenses, or create a new one.</p>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 h-12 px-6 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors"
            data-testid="create-project-button"
          >
            <Plus size={16} weight="bold" /> New project
          </button>
        </div>

        {billing && (() => {
          const tier = billing.tier || "free";
          const max = billing.limits?.max_projects ?? 0;
          const used = billing.usage?.projects ?? 0;
          const unlimited = max >= 9999;
          // Free tier always shows the friendly demo banner below — skip the harsh
          // "you've hit the limit" plan-usage banner (limit is 0 by design, not a wall).
          if (tier === "free") return null;
          const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, max)) * 100));
          const state = unlimited ? "ok" : pct >= 100 ? "full" : pct >= 80 ? "warn" : "ok";
          const tone = {
            ok: { bg: "#FFFFFF", border: "#E4E4E7", text: "#09090B", accent: "#0020F5" },
            warn: { bg: "#FFF7E6", border: "#FF9500", text: "#7A4500", accent: "#FF9500" },
            full: { bg: "#FFE5E3", border: "#FF3B30", text: "#7A1C16", accent: "#FF3B30" },
          }[state];
          return (
            <div
              className="border p-5 mb-8 flex flex-wrap items-center justify-between gap-4"
              style={{ background: tone.bg, borderColor: tone.border }}
              data-testid="plan-usage-banner"
            >
              <div className="flex items-center gap-4 min-w-0">
                {state === "full" ? <Warning size={22} weight="fill" style={{ color: tone.accent }} />
                  : state === "warn" ? <Warning size={22} weight="duotone" style={{ color: tone.accent }} />
                  : <Sparkle size={20} weight="fill" style={{ color: tone.accent }} />}
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: tone.text, opacity: 0.7 }}>
                    Current plan · {TIER_LABEL[tier]}
                  </div>
                  <div className="font-display font-bold text-lg mt-0.5" style={{ color: tone.text }}>
                    {unlimited
                      ? `${used} projects · Unlimited on Enterprise`
                      : state === "full"
                      ? `You've hit the limit — ${used} / ${max} projects on ${TIER_LABEL[tier]}`
                      : state === "warn"
                      ? `${used} / ${max} projects used on ${TIER_LABEL[tier]} — upgrade before you hit the wall`
                      : `${used} / ${max} projects used on ${TIER_LABEL[tier]}`}
                  </div>
                  {!unlimited && (
                    <div className="mt-2 w-64 max-w-full h-1.5 bg-white border" style={{ borderColor: tone.border }}>
                      <div className="h-full" style={{ width: `${pct}%`, background: tone.accent }} />
                    </div>
                  )}
                </div>
              </div>
              {!unlimited && (
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 h-11 px-5 text-sm font-bold transition-colors"
                  style={{ background: tone.accent, color: "#FFFFFF" }}
                  data-testid="banner-upgrade-link"
                >
                  {state === "full" ? "Upgrade now" : "View plans"} <ArrowRight size={14} weight="bold" />
                </Link>
              )}
            </div>
          );
        })()}

        {user && (user.subscription_tier === "free" || !user.subscription_tier) && !user.is_admin && (
          <div
            className="border-l-4 p-4 mb-8 flex flex-wrap items-center gap-4"
            style={{ borderColor: "#0020F5", background: "#E5EAFF" }}
            data-testid="free-demo-banner"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: "#0020F5" }}>
                Free demo
              </div>
              <div className="font-display font-bold text-base mt-0.5 text-zinc-950">
                You're on a free guided tour — explore the demo project below to see how ARISSAI works.
                <span className="text-zinc-500 font-normal text-sm font-sans"> Bring your own data on Starter ($9.99/mo) when you're ready.</span>
              </div>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 h-10 px-5 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5] transition-colors"
              data-testid="free-demo-upgrade"
            >
              Upgrade →
            </Link>
          </div>
        )}

        {aiUsage && aiUsage.limit !== 0 && (() => {
          const unlimited = aiUsage.limit === -1;
          const pct = unlimited ? 0 : Math.min(100, Math.round((aiUsage.used / Math.max(1, aiUsage.limit)) * 100));
          const accent = pct >= 100 ? "#FF3B30" : pct >= 80 ? "#FF9500" : "#0020F5";
          return (
            <div className="border border-zinc-200 bg-white p-4 mb-8 flex flex-wrap items-center gap-4" data-testid="ai-usage-banner">
              <Sparkle size={18} weight="fill" style={{ color: accent }} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">AI usage · {aiUsage.month}</div>
                <div className="font-display font-bold text-base mt-0.5">
                  {unlimited
                    ? `${aiUsage.used} AI calls used · Unlimited on Enterprise`
                    : `${aiUsage.used} / ${aiUsage.limit} AI calls used this month`}
                </div>
                {!unlimited && (
                  <div className="mt-2 w-64 max-w-full h-1.5 bg-zinc-100">
                    <div className="h-full" style={{ width: `${pct}%`, background: accent }} />
                  </div>
                )}
              </div>
              {!unlimited && pct >= 80 && (
                <Link to="/pricing" className="text-xs font-bold text-[#0020F5] hover:underline" data-testid="ai-usage-upgrade">
                  Upgrade for more →
                </Link>
              )}
            </div>
          );
        })()}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-44 bg-white border border-zinc-200 animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-16 text-center" data-testid="empty-projects">
            <FolderOpen size={40} weight="duotone" className="mx-auto text-zinc-400" />
            <h3 className="font-display font-bold text-2xl mt-4">No projects yet</h3>
            <p className="text-zinc-600 mt-2 max-w-md mx-auto">
              Create your first project to start validating assumptions, forecasting risks, and generating AI-driven solutions.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800"
              data-testid="empty-create-button"
            >
              <Plus size={16} weight="bold" /> Create project
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {projects.map((p, i) => (
              <div key={p.id} className="bg-white p-7 group relative" data-testid={`project-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400">#{String(i + 1).padStart(3, "0")}</div>
                  {p.industry && p.industry !== "general" && (
                    <span
                      className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.15em] uppercase font-bold px-2 h-5 text-white"
                      style={{ background: getIndustry(p.industry).accent }}
                      data-testid={`project-industry-badge-${p.id}`}
                    >
                      <Buildings size={9} weight="fill" />
                      {getIndustry(p.industry).label}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-2xl mt-3 line-clamp-2">{p.name}</h3>
                <p className="text-sm text-zinc-600 mt-2 line-clamp-3 min-h-[60px]">{p.description || "—"}</p>
                <div className="mt-6 flex items-center justify-between">
                  <Link
                    to={`/projects/${p.id}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-950 hover:text-[#0020F5]"
                    data-testid={`open-project-${p.id}`}
                  >
                    Open <ArrowRight size={14} weight="bold" />
                  </Link>
                  <button
                    onClick={() => remove(p.id)}
                    className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-[#FF3B30] transition-opacity"
                    data-testid={`delete-project-${p.id}`}
                    aria-label="Delete project"
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !creating && setOpen(false)}>
          <form
            onSubmit={create}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-zinc-300 w-full max-w-lg p-8"
            data-testid="create-project-modal"
          >
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">New project</div>
            <h2 className="font-display font-black tracking-tighter text-3xl mt-2">Start a workspace</h2>

            <label className="block mt-6 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Project name</label>
            <input
              required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
              placeholder="Enterprise CRM migration"
              data-testid="project-name-input"
            />

            <label className="block mt-5 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Description</label>
            <textarea
              value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
              className="w-full mt-2 p-3 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none"
              placeholder="Briefly describe scope and objectives so the AI lenses have context."
              data-testid="project-desc-input"
            />

            <label className="block mt-5 text-xs tracking-[0.2em] uppercase font-bold text-zinc-600">Industry</label>
            <select
              value={projectIndustry}
              onChange={(e) => setProjectIndustry(e.target.value)}
              className="w-full mt-2 h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
              data-testid="project-industry-select"
            >
              {INDUSTRIES.map((i) => (
                <option key={i.slug} value={i.slug}>{i.label}</option>
              ))}
            </select>
            <p className="mt-1.5 text-[11px] text-zinc-500">Tailors AI lens vocabulary to your domain.</p>

            <div className="mt-7 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} disabled={creating}
                className="h-11 px-5 border border-zinc-300 text-sm font-medium hover:bg-zinc-100"
                data-testid="cancel-create-button">Cancel</button>
              <button type="submit" disabled={creating}
                className="h-11 px-5 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50"
                data-testid="confirm-create-button">
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
